import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Calendar, MapPin, ChevronRight, Plus, Compass } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { membershipService } from '../services/dataService';
import type { MyEvent } from '../services/dataService';
import { notify } from '../utils/notifications';
import { formatEventDateRange } from '../utils/scheduleTime';

export default function MyConferencesScreen() {
  const navigate = useNavigate();
  const {
    currentEventId,
    setCurrentEventId,
    authSession,
    isAuthSessionCurrent,
  } = useApp();
  const [events, setEvents] = useState<MyEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const owner = authSession;
    if (!owner || !isAuthSessionCurrent(owner)) return;
    let disposed = false;
    setEvents([]);
    setLoading(true);
    membershipService.listMyEvents({ signal: owner.signal })
      .then((data) => {
        if (!disposed && isAuthSessionCurrent(owner)) setEvents(data);
      })
      .catch(() => {
        if (!disposed && isAuthSessionCurrent(owner)) {
          notify.error('Could not load your conferences');
        }
      })
      .finally(() => {
        if (!disposed && isAuthSessionCurrent(owner)) setLoading(false);
      });
    return () => { disposed = true; };
  }, [authSession, isAuthSessionCurrent]);

  function selectEvent(id: string) {
    setCurrentEventId(id);
    localStorage.setItem('current-event-id', id);
    navigate('/home', { replace: true });
  }

  return (
    <div
      className="size-full flex flex-col overflow-y-auto"
      style={{ backgroundColor: 'var(--app-bg)' }}
    >
      {/* Header */}
      <div className="app-screen-header px-6 pt-14 pb-4">
        <h1 className="text-[28px] font-semibold tracking-tight" style={{ color: 'var(--app-text)' }}>
          My Conferences
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--app-text-muted)' }}>
          Select a conference to explore, or join a new one.
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 pb-6">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center px-4">
            <Calendar className="w-12 h-12 mb-4" style={{ color: 'var(--app-text-subtle)' }} />
            <p className="text-lg font-medium mb-2" style={{ color: 'var(--app-text)' }}>No conferences yet</p>
            <p className="text-sm mb-6" style={{ color: 'var(--app-text-muted)' }}>
              Join a conference with an invite code, or browse public events to get started.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {events.map((event) => (
              <div
                key={event.id}
                className="w-full overflow-hidden rounded-2xl transition-all"
                style={{
                  backgroundColor: currentEventId === event.id
                    ? 'var(--app-accent-dim)'
                    : 'var(--app-card-bg)',
                  border: `1px solid ${currentEventId === event.id
                    ? 'var(--app-accent-border)'
                    : 'var(--app-border)'}`,
                }}
              >
                <button
                  onClick={() => selectEvent(event.id)}
                  aria-label={`Select ${event.name}`}
                  className="w-full p-4 text-left active:scale-[0.99]"
                >
                  <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-base font-semibold truncate" style={{ color: 'var(--app-text)' }}>
                        {event.name}
                      </p>
                      {currentEventId === event.id && (
                        <span
                          className="text-xs px-2 py-0.5 rounded-full shrink-0"
                          style={{ backgroundColor: 'var(--app-accent-dim)', color: 'var(--app-accent)' }}
                        >
                          Active
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs" style={{ color: 'var(--app-text-muted)' }}>
                      <span className="flex items-center gap-1">
                        <Calendar size={11} />
                        {formatEventDateRange(event.date_start, event.date_end)}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin size={11} />
                        {event.location}
                      </span>
                    </div>
                    <p className="text-xs mt-1 capitalize" style={{ color: 'var(--app-text-subtle)' }}>
                      {event.ticket_type} · {event.role}
                    </p>
                  </div>
                  <ChevronRight size={16} style={{ color: 'var(--app-text-subtle)' }} className="mt-1 shrink-0" />
                  </div>
                </button>
                <div className="border-t px-4 py-2" style={{ borderColor: 'var(--app-border)' }}>
                  <button
                    onClick={() => navigate(`/event/${event.id}`)}
                    aria-label={`Event details for ${event.name}`}
                    className="text-xs font-medium"
                    style={{ color: 'var(--app-accent)' }}
                  >
                    Event details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom actions */}
      <div className="px-4 pb-10 pt-2 flex flex-col gap-3 border-t" style={{ borderColor: 'var(--app-border)' }}>
        <button
          onClick={() => navigate('/join')}
          className="h-14 rounded-xl text-base flex items-center justify-center gap-2 active:scale-95 transition-transform"
          style={{
            backgroundColor: 'var(--app-accent-solid)',
            boxShadow: '0 8px 16px var(--app-accent-glow)',
            color: '#fff',
          }}
        >
          <Plus size={18} />
          Join with Invite Code
        </button>

        <button
          onClick={() => navigate('/discover')}
          className="h-12 rounded-xl text-base flex items-center justify-center gap-2 active:opacity-70 transition-opacity"
          style={{
            backgroundColor: 'var(--app-card-bg)',
            border: '1px solid var(--app-border)',
            color: 'var(--app-text-muted)',
          }}
        >
          <Compass size={16} />
          Browse public conferences
        </button>
      </div>
    </div>
  );
}
